import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { IAssignServiceProvider } from "@/api/assignServiceProvider/assignServiceProvider.queries.type";

type AssignServiceProviderState = {
  orderedProviders: IAssignServiceProvider[];
  currentProviderId: string | null;
  resolvedProviderName: string | null;
  worksiteTimezone: string | null;
  // SERV-2715: true while the provider list's footer shows its "Request to Add
  // New Service Provider" button. The centre pane's exhausted-network card
  // renders its own copy of that button only while this is false, so the
  // screen never shows two.
  listHasRequestProviderButton: boolean;
  setOrderedProviders: (providers: IAssignServiceProvider[]) => void;
  setCurrentProviderId: (id: string | null) => void;
  setResolvedProviderName: (name: string | null) => void;
  setWorksiteTimezone: (timezone: string | null) => void;
  setListHasRequestProviderButton: (visible: boolean) => void;
  getNextProviderId: () => string | null;
  getCurrentProviderIndex: () => number;
  hasNextProvider: () => boolean;
  resetAssignServiceProviderStore: () => void;
};

export const useAssignServiceProviderStore = create<AssignServiceProviderState>()(
  persist(
    (set, get) => ({
      orderedProviders: [],
      currentProviderId: null,
      resolvedProviderName: null,
      worksiteTimezone: null,
      listHasRequestProviderButton: true,

      setOrderedProviders: (providers) => set({ orderedProviders: providers }),

      setCurrentProviderId: (id) => set({ currentProviderId: id }),

      setResolvedProviderName: (name) => set({ resolvedProviderName: name }),

      setWorksiteTimezone: (timezone) => set({ worksiteTimezone: timezone }),

      setListHasRequestProviderButton: (visible) => set({ listHasRequestProviderButton: visible }),

      getCurrentProviderIndex: () => {
        const { orderedProviders, currentProviderId } = get();
        if (!currentProviderId) return -1;
        return orderedProviders.findIndex((sp) => sp.id === currentProviderId);
      },

      getNextProviderId: () => {
        const { orderedProviders, getCurrentProviderIndex } = get();
        const currentIndex = getCurrentProviderIndex();

        if (currentIndex === -1 || currentIndex === orderedProviders.length - 1) {
          return null;
        }

        const nextProvider = orderedProviders[currentIndex + 1];
        return nextProvider?.id || null;
      },

      hasNextProvider: () => {
        const { orderedProviders, getCurrentProviderIndex } = get();
        const currentIndex = getCurrentProviderIndex();
        return currentIndex !== -1 && currentIndex < orderedProviders.length - 1;
      },

      resetAssignServiceProviderStore: () =>
        set({
          orderedProviders: [],
          currentProviderId: null,
          resolvedProviderName: null,
          worksiteTimezone: null,
          listHasRequestProviderButton: true,
        }),
    }),
    {
      name: "assign-service-provider-store",
      partialize: (state) => ({
        orderedProviders: state.orderedProviders,
        currentProviderId: state.currentProviderId,
        resolvedProviderName: state.resolvedProviderName,
      }),
    },
  ),
);
